import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { MessagesSquare, Search } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useGetConversationsQuery } from '@/store/api/agentRuntimeApi';
import { useGetAgentFactoriesQuery } from '@/store/api/agentFactoryApi';
import { useGetProjectsQuery } from '@/store/api/projectsApi';
import type { Conversation, ConversationStatus } from '@/types';

const STATUS_VARIANT: Record<ConversationStatus, 'success' | 'secondary' | 'warning' | 'destructive' | 'outline'> = {
  pending: 'warning',
  running: 'outline',
  idle: 'secondary',
  paused: 'warning',
  stopped: 'secondary',
  completed: 'success',
  error: 'destructive',
};

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'idle', label: 'Idle' },
  { value: 'paused', label: 'Paused' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'completed', label: 'Completed' },
  { value: 'error', label: 'Error' },
];

export function ConversationsPage() {
  const navigate = useNavigate();
  const [factoryId, setFactoryId] = useState('');
  const [status, setStatus] = useState('');
  const [projectId, setProjectId] = useState('');
  const [search, setSearch] = useState('');

  const { data: factories } = useGetAgentFactoriesQuery();
  const { data: projects } = useGetProjectsQuery();

  const { data, isLoading, isError } = useGetConversationsQuery({
    factoryId: factoryId || undefined,
    status: status || undefined,
    projectId: projectId || undefined,
    limit: 100,
  });
  const conversations = data?.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) =>
        (c.name ?? '').toLowerCase().includes(q) || c._id.toLowerCase().includes(q),
    );
  }, [conversations, search]);

  const columns: ColumnDef<Conversation, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Conversation',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <MessagesSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="truncate font-medium">
                {row.original.name || row.original._id}
              </p>
              <p className="truncate font-mono text-xs text-muted-foreground">{row.original._id}</p>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'factoryId',
        header: 'Factory',
        cell: ({ row }) => (
          <span className="truncate text-sm text-muted-foreground">
            {(factories ?? []).find((f) => f._id === row.original.factoryId)?.name ??
              row.original.factoryId.slice(0, 8)}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANT[row.original.status] ?? 'outline'}>
            {row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: 'usage.totalTokens',
        header: 'Usage',
        cell: ({ row }) => {
          const u = row.original.usage;
          if (!u || u.totalTokens == null) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="tabular-nums">
              {u.totalTokens.toLocaleString()}
              <span className="ml-1 text-xs text-muted-foreground">tok</span>
            </span>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, HH:mm'),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [factories],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Conversations</h1>
        <p className="text-sm text-muted-foreground">
          Agent conversation runs — inspect messages, control execution, and read reasoning.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[14rem]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name / id..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={factoryId} onValueChange={setFactoryId}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All factories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All factories</SelectItem>
                {(factories ?? []).map((f) => (
                  <SelectItem key={f._id} value={f._id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={status || '__all__'}
              onValueChange={(v) => setStatus(v === '__all__' ? '' : v)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.filter((s) => s.value !== '').map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
                <SelectItem value="__all__">All statuses</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={projectId || '__all__'}
              onValueChange={(v) => setProjectId(v === '__all__' ? '' : v)}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All projects</SelectItem>
                {(projects ?? []).map((p) => (
                  <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(factoryId || status || projectId) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFactoryId('');
                  setStatus('');
                  setProjectId('');
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : isError ? (
            <p className="py-8 text-center text-sm text-destructive">Failed to load conversations.</p>
          ) : filtered.length === 0 ? (
            <EmptyState
              title="No conversations"
              description={search || factoryId || status || projectId ? 'No conversations match the filters.' : 'Dispatch a conversation from a factory to see it here.'}
            />
          ) : (
            <DataTable
              columns={columns}
              data={filtered}
              onRowClick={(c) => navigate(`/conversations/${c._id}`)}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}